import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  X, Copy, Check, ExternalLink, Download, Facebook, Linkedin,
  MessageCircle, Sparkles, Image as ImageIcon, Info,
} from "lucide-react";

/**
 * Modal de partage social — pour toute actualité (nouvelle ou ancienne).
 * Composer réutilisable :
 *  - Génère automatiquement un texte de publication (titre + extrait + hashtags + lien)
 *  - Prévisualise l'image principale et permet de la télécharger
 *  - Fournit un bouton par plateforme :
 *      Facebook, X (Twitter), LinkedIn, WhatsApp  → partage direct (URL scheme)
 *      Instagram, TikTok                          → copie + ouverture de l'app (les APIs
 *                                                    officielles ne permettent PAS le
 *                                                    partage direct d'une URL externe)
 */

const DEFAULT_HASHTAGS = "#FrontUniRDC #RDC #CongoRDC #Diaspora #Plaidoyer";

// Domaine officiel forcé pour les liens (indépendamment du preview)
const PUBLIC_DOMAIN = "https://frontunirdc.be";

function buildShareUrl(slug) {
  // Utilise l'endpoint /api/share/news/{slug} qui renvoie les balises Open Graph
  // dynamiques (titre, image, description) pour Facebook / WhatsApp / X / LinkedIn.
  // Les humains sont redirigés automatiquement vers /actualites/{slug}.
  return `${PUBLIC_DOMAIN}/api/share/news/${slug}`;
}

function buildText(news, hashtags = DEFAULT_HASHTAGS) {
  const excerpt = (news.excerpt || "").trim();
  const parts = [news.title.trim()];
  if (excerpt) parts.push(excerpt.length > 200 ? excerpt.slice(0, 197) + "…" : excerpt);
  parts.push(hashtags.trim());
  return parts.filter(Boolean).join("\n\n");
}

export default function SocialShareModal({ news, onClose }) {
  const shareUrl = useMemo(() => buildShareUrl(news.slug), [news.slug]);
  const [text, setText] = useState(() => buildText(news));
  const [hashtags, setHashtags] = useState(DEFAULT_HASHTAGS);
  const [copied, setCopied] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    setText(buildText(news, hashtags));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hashtags, news.id]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const encText = encodeURIComponent(text);
  const encUrl = encodeURIComponent(shareUrl);
  const encTitle = encodeURIComponent(news.title || "");

  const platforms = [
    {
      key: "facebook",
      label: "Facebook",
      color: "bg-[#1877F2] hover:bg-[#0d5db3]",
      Icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}&quote=${encText}`,
      mode: "direct",
      note: "Partage direct — texte + lien",
    },
    {
      key: "twitter",
      label: "X (Twitter)",
      color: "bg-black hover:bg-neutral-800",
      Icon: TwitterXIcon,
      href: `https://twitter.com/intent/tweet?text=${encText}&url=${encUrl}`,
      mode: "direct",
      note: "Partage direct — pense-bête 280 caractères",
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      color: "bg-[#0A66C2] hover:bg-[#004182]",
      Icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`,
      mode: "direct",
      note: "Partage direct — LinkedIn reprend l'aperçu du lien",
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      color: "bg-[#25D366] hover:bg-[#128C7E]",
      Icon: WhatsAppIcon,
      href: `https://wa.me/?text=${encText}%20${encUrl}`,
      mode: "direct",
      note: "Partage direct — statut, groupes, contacts",
    },
    {
      key: "instagram",
      label: "Instagram",
      color: "bg-gradient-to-tr from-[#f09433] via-[#e6683c] via-[#dc2743] to-[#cc2366] hover:opacity-90",
      Icon: InstagramIcon,
      href: "https://www.instagram.com/",
      mode: "copy",
      note: "L'API Instagram ne permet pas le partage direct → texte copié + ouverture de l'app.",
    },
    {
      key: "tiktok",
      label: "TikTok",
      color: "bg-black hover:bg-neutral-800",
      Icon: TikTokIcon,
      href: "https://www.tiktok.com/",
      mode: "copy",
      note: "TikTok ne permet pas le partage direct → texte copié + ouverture de l'app.",
    },
  ];

  const copyText = async (value = text, key = "text") => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(key);
      setTimeout(() => setCopiedField(null), 1500);
      toast.success("Texte copié dans le presse-papiers");
    } catch {
      toast.error("Copie impossible");
    }
  };

  const openPlatform = async (p) => {
    if (p.mode === "copy") {
      // Copie automatiquement le texte puis ouvre l'app
      try {
        await navigator.clipboard.writeText(`${text}\n\n${shareUrl}`);
        toast.success(`Texte copié — ouvrez ${p.label} et collez dans votre publication`);
      } catch {
        toast.warning("Copie automatique impossible — utilisez le bouton Copier ci-dessous");
      }
    }
    window.open(p.href, "_blank", "noopener,noreferrer");
  };

  const downloadImage = () => {
    if (!news.cover_image) return toast.warning("Aucune image principale");
    window.open(news.cover_image, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed inset-0 z-[95] grid place-items-center p-4 sm:p-6" onClick={onClose} data-testid="social-share-modal">
      <div className="absolute inset-0 bg-ink/80 backdrop-blur-md" />
      <div className="relative bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="min-w-0">
            <div className="eyebrow text-gold-dark flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Partager sur les réseaux sociaux</div>
            <h3 className="font-serif text-xl text-ink mt-1 truncate max-w-md">{news.title}</h3>
          </div>
          <button onClick={onClose} className="text-foreground/60 hover:text-ink" data-testid="social-share-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Aperçu */}
          <div className="grid md:grid-cols-[200px,1fr] gap-4 items-start bg-bone rounded-sm p-4 border border-border">
            <div className="aspect-square bg-white rounded-sm overflow-hidden border border-border">
              {news.cover_image ? (
                <img src={news.cover_image} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full grid place-items-center text-foreground/25">
                  <ImageIcon className="w-8 h-8" />
                </div>
              )}
            </div>
            <div className="min-w-0">
              <div className="eyebrow text-gold-dark text-[0.62rem]">{news.category}</div>
              <div className="font-serif text-lg text-ink leading-tight mt-1 line-clamp-2">{news.title}</div>
              <div className="mt-3 text-xs text-foreground/60 line-clamp-3">{news.excerpt}</div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button onClick={() => copyText(shareUrl, "url")} className="btn-outline text-xs" data-testid="social-copy-link">
                  {copiedField === "url" ? <><Check className="w-3.5 h-3.5 text-emerald-600" /> Lien copié</> : <><Copy className="w-3.5 h-3.5" /> Copier le lien</>}
                </button>
                {news.cover_image && (
                  <button onClick={downloadImage} className="btn-outline text-xs" data-testid="social-download-image">
                    <Download className="w-3.5 h-3.5" /> Ouvrir l'image
                  </button>
                )}
              </div>
              <div className="mt-2 text-[0.68rem] font-mono text-foreground/50 truncate">{shareUrl}</div>
            </div>
          </div>

          {/* Texte éditable */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60">Texte de publication</label>
              <button onClick={() => copyText(text, "text")} className="text-xs font-semibold text-ink inline-flex items-center gap-1.5 hover:text-gold-dark" data-testid="social-copy-text">
                {copiedField === "text" ? <><Check className="w-3.5 h-3.5 text-emerald-600" /> Copié</> : <><Copy className="w-3.5 h-3.5" /> Copier le texte</>}
              </button>
            </div>
            <textarea rows={6} value={text} onChange={(e) => setText(e.target.value)}
                      className="inp font-serif" data-testid="social-text" />
            <div className="mt-2 flex items-center justify-between text-[0.7rem] text-foreground/55">
              <span>{text.length} caractères · {countHashtags(text)} hashtags</span>
              <button onClick={() => setText(buildText(news, hashtags))} className="underline hover:text-ink" data-testid="social-regenerate">
                Régénérer automatiquement
              </button>
            </div>
          </div>

          {/* Hashtags */}
          <div>
            <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-1.5">Hashtags par défaut</label>
            <input value={hashtags} onChange={(e) => setHashtags(e.target.value)} className="inp text-sm" data-testid="social-hashtags" />
          </div>

          {/* Plateformes */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <ExternalLink className="w-3.5 h-3.5 text-gold-dark" />
              <div className="eyebrow text-gold-dark">Choisir une plateforme</div>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {platforms.map((p) => (
                <button
                  key={p.key}
                  onClick={() => openPlatform(p)}
                  className={`group relative flex items-center gap-3 rounded-sm text-white p-4 transition-all shadow-sm hover:shadow-lg hover:-translate-y-0.5 ${p.color}`}
                  data-testid={`social-share-${p.key}`}
                >
                  <p.Icon className="w-6 h-6 shrink-0" />
                  <div className="min-w-0 text-left">
                    <div className="font-semibold text-sm">{p.label}</div>
                    <div className="text-[0.68rem] text-white/85 line-clamp-2">{p.note}</div>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 opacity-70 shrink-0 ml-auto" />
                </button>
              ))}
            </div>
            <div className="mt-4 rounded-sm bg-foreground/[0.04] border border-border p-3 text-xs text-foreground/70 flex gap-2">
              <Info className="w-4 h-4 text-gold-dark shrink-0 mt-0.5" />
              <div>
                <strong>Bon à savoir :</strong> Facebook, X, LinkedIn et WhatsApp ouvrent une fenêtre pré-remplie.
                Instagram et TikTok ne permettent pas le partage direct depuis un site externe : le texte est
                automatiquement copié, il vous suffira de le coller dans votre publication.
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-border px-6 py-4 flex justify-end">
          <button onClick={onClose} className="btn-outline text-sm" data-testid="social-share-done">Fermer</button>
        </div>
      </div>
    </div>
  );
}

function countHashtags(str) {
  return (str.match(/#\w+/g) || []).length;
}

/* Icônes non standards */
function WhatsAppIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.472-.148-.67.15-.198.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}
function TwitterXIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}
function InstagramIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12 2.163c3.204 0 3.584.012 4.849.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.849.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  );
}
function TikTokIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  );
}
